import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * VRG-282 — the brand product a package of tags is pre-assigned to.
 *
 * Deliberately a THIN projection (not the full `Product`): the packages payload carries only
 * what a preview row needs, and typing it as `Product` here would promise fields the list
 * endpoint does not return.
 */
export type PackageBoundProduct = {
  id: string
  title: string
  sku: string | null
  image: string | null
}

/**
 * The package holds a certificate that is already ACTIVATED, so the binding is frozen — that
 * certificate is live, possibly transferred, and may already be billed. This is a legitimate,
 * reachable outcome of a rebind, not a transport failure, so it gets surfaced verbatim rather
 * than collapsed into "something went wrong".
 */
export const PACKAGE_PRODUCT_CONFLICT_STATUS = 409

/** An Error carrying the HTTP status, so callers can tell a 409 lock from a generic failure. */
export type PackageProductError = Error & { status?: number }

type AssignPackageProductParams = {
  id: string
  /** `null` unbinds the package. */
  productId: string | null
}

/**
 * PATCH /packages/:id/product — bind (or re-point, or clear) the brand product a whole package
 * of tags is pre-assigned to.
 *
 * Binding records INTENT; certificates are created as the box is filled (see the task's
 * "Creation timing" section), so this is a cheap write even for an empty package.
 */
export const useAssignPackageProduct = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['packages', 'assign-product'],
    mutationFn: async ({ id, productId }: AssignPackageProductParams) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/packages/${id}/product`,
        data: { productId },
      })
      // `createApiCall`'s errorHandler RESOLVES with `error.response` on 4xx/5xx rather than
      // rejecting (same trap documented in `useUpdatePackage`), so without this check a 409
      // would read as a successful rebind and the modal would close on a refusal.
      const status = response?.status
      if (typeof status === 'number' && (status < 200 || status >= 300)) {
        const error: PackageProductError = new Error(
          (response?.data as any)?.message ||
            (status === PACKAGE_PRODUCT_CONFLICT_STATUS
              ? 'A certificate in this package has already been activated, so it can no longer be pointed at a different product.'
              : 'Could not assign the product'),
        )
        error.status = status
        throw error
      }
      return response.data as { id: string; product?: PackageBoundProduct | null }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] })
    },
  })
}
